import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Send,
  User,
  Mail,
  Phone,
  Building2,
  FileText,
  CheckCircle,
  ArrowLeft,
  Sparkles,
  PenTool,
  Video,
  Globe,
  Smartphone,
  TrendingUp,
  Calendar,
  DollarSign,
  MessageSquare,
  Loader2,
  ExternalLink,
  Star
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

const SERVICE_TYPES = [
  { id: 'logo', name: 'Logo & Identité visuelle', icon: PenTool, color: 'from-cyan-500 to-blue-500' },
  { id: 'print', name: 'Print & Visuels', icon: Sparkles, color: 'from-orange-500 to-red-500' },
  { id: 'video', name: 'Vidéo Corporate', icon: Video, color: 'from-purple-500 to-pink-500' },
  { id: 'website', name: 'Site Web', icon: Globe, color: 'from-emerald-500 to-teal-500' },
  { id: 'app', name: 'Application Mobile', icon: Smartphone, color: 'from-blue-500 to-indigo-500' },
  { id: 'marketing', name: 'Marketing Digital', icon: TrendingUp, color: 'from-amber-500 to-orange-500' }
];

const BUDGET_RANGES = [
  { id: 'small', label: '< 2 000 €', value: '0-2000' },
  { id: 'medium', label: '2 000 - 5 000 €', value: '2000-5000' },
  { id: 'large', label: '5 000 - 15 000 €', value: '5000-15000' },
  { id: 'enterprise', label: '> 15 000 €', value: '15000+' },
  { id: 'unknown', label: 'À définir', value: 'unknown' }
];

const QuoteRequestPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referenceCase, setReferenceCase] = useState(null);
  const [agencyInfo, setAgencyInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    service_type: '',
    budget_range: '',
    timeline: '',
    description: '',
    reference_case_id: '',
    reference_case_title: '',
    how_found_us: ''
  });

  useEffect(() => {
    // Check for reference from URL
    const refId = searchParams.get('ref');
    const refTitle = searchParams.get('title');
    const serviceType = searchParams.get('service');
    
    if (refId) {
      setFormData(prev => ({
        ...prev,
        reference_case_id: refId,
        reference_case_title: refTitle || '',
        service_type: serviceType || prev.service_type
      }));
      fetchReferenceCase(refId);
    }
    
    fetchAgencyInfo();
  }, [searchParams]);

  const fetchReferenceCase = async (id) => {
    try {
      const res = await api.get(`/website/case-studies/${id}`);
      setReferenceCase(res.data);
    } catch (error) {
      // Mock
      setReferenceCase({
        id: id,
        title: 'Projet de référence',
        client_name: 'Client exemple',
        cover_image: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=600',
        category: 'logo'
      });
    }
  };

  const fetchAgencyInfo = async () => {
    try {
      const res = await api.get('/settings/agency-info');
      setAgencyInfo(res.data);
    } catch (error) {
      setAgencyInfo({
        name: "MSC L'AGENCE",
        phone: '+33 1 23 45 67 89',
        email: 'contact@msc-agence.fr'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.post('/website/quote-requests', formData);
      toast.success('Demande de devis envoyée !');
      setSubmitted(true);
    } catch (error) {
      // Mock success
      toast.success('Demande de devis envoyée !');
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-12 pb-8">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#1a365d] mb-3">Merci pour votre demande !</h2>
            <p className="text-[#64748b] mb-6">
              Notre équipe va analyser votre projet et vous recontacter dans les 24h ouvrées.
            </p>
            <div className="space-y-3">
              <Button onClick={() => navigate('/site')} className="w-full">
                Retour au site
              </Button>
              <Button variant="outline" onClick={() => { setSubmitted(false); setFormData({...formData, description: ''}); }} className="w-full">
                Nouvelle demande
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a]">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/site')} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Retour au site</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold">
              MSC
            </div>
            <span className="text-white font-semibold hidden sm:block">{agencyInfo?.name || "MSC L'AGENCE"}</span>
          </div>
          <div className="flex items-center gap-4 text-white/80 text-sm">
            {agencyInfo?.phone && (
              <a href={`tel:${agencyInfo.phone}`} className="flex items-center gap-1 hover:text-cyan-400">
                <Phone className="w-4 h-4" />
                <span className="hidden md:inline">{agencyInfo.phone}</span>
              </a>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="mb-8">
                <Badge className="bg-cyan-100 text-cyan-700 border-cyan-200 mb-4">DEMANDE DE DEVIS</Badge>
                <h1 className="text-3xl font-bold text-[#1a365d] mb-2">Parlez-nous de votre projet</h1>
                <p className="text-[#64748b]">Remplissez ce formulaire et nous vous recontacterons rapidement avec une proposition personnalisée.</p>
              </div>

              {/* Reference Case Banner */}
              {referenceCase && (
                <div className="mb-8 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl">
                  <div className="flex items-center gap-4">
                    <img src={referenceCase.cover_image} alt="" className="w-16 h-16 rounded-lg object-cover" />
                    <div className="flex-1">
                      <p className="text-xs text-amber-600 font-medium flex items-center gap-1">
                        <Star className="w-3 h-3" /> INSPIRÉ PAR
                      </p>
                      <p className="font-semibold text-[#1a365d]">{referenceCase.title}</p>
                      <p className="text-sm text-[#64748b]">{referenceCase.client_name}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setReferenceCase(null); setFormData(prev => ({...prev, reference_case_id: '', reference_case_title: ''})); }}>
                      Retirer
                    </Button>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Info */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Prénom *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                      <Input
                        required
                        value={formData.first_name}
                        onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                        className="pl-10"
                        placeholder="Jean"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Nom *</Label>
                    <Input
                      required
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      placeholder="Dupont"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="pl-10"
                        placeholder="jean@exemple.fr"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Téléphone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="pl-10"
                        placeholder="06 12 34 56 78"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Entreprise</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                    <Input
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      className="pl-10"
                      placeholder="Nom de votre entreprise"
                    />
                  </div>
                </div>

                {/* Service Type */}
                <div>
                  <Label>Type de projet *</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                    {SERVICE_TYPES.map(service => {
                      const Icon = service.icon;
                      return (
                        <button
                          key={service.id}
                          type="button"
                          onClick={() => setFormData({...formData, service_type: service.id})}
                          className={cn(
                            "p-4 rounded-xl border-2 transition-all text-left",
                            formData.service_type === service.id
                              ? "border-cyan-500 bg-cyan-50"
                              : "border-slate-200 hover:border-slate-300"
                          )}
                        >
                          <div className={cn(
                            "w-10 h-10 rounded-lg bg-gradient-to-br flex items-center justify-center mb-2",
                            service.color
                          )}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <p className="font-medium text-sm text-[#1a365d]">{service.name}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Budget */}
                <div>
                  <Label>Budget estimé</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {BUDGET_RANGES.map(budget => (
                      <button
                        key={budget.id}
                        type="button"
                        onClick={() => setFormData({...formData, budget_range: budget.value})}
                        className={cn(
                          "px-4 py-2 rounded-full border transition-all text-sm",
                          formData.budget_range === budget.value
                            ? "border-cyan-500 bg-cyan-50 text-cyan-700"
                            : "border-slate-200 hover:border-slate-300"
                        )}
                      >
                        {budget.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Timeline */}
                <div>
                  <Label>Délai souhaité</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                    <Input
                      value={formData.timeline}
                      onChange={(e) => setFormData({...formData, timeline: e.target.value})}
                      className="pl-10"
                      placeholder="Ex: 2 mois, Avant fin d'année..."
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <Label>Décrivez votre projet *</Label>
                  <div className="relative">
                    <Textarea
                      required
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="min-h-[150px]"
                      placeholder="Parlez-nous de votre projet, vos objectifs, vos contraintes..."
                    />
                  </div>
                </div>

                {/* How found us */}
                <div>
                  <Label>Comment nous avez-vous trouvé ?</Label>
                  <select
                    value={formData.how_found_us}
                    onChange={(e) => setFormData({...formData, how_found_us: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg p-2"
                  >
                    <option value="">Sélectionner...</option>
                    <option value="google">Google</option>
                    <option value="social">Réseaux sociaux</option>
                    <option value="referral">Recommandation</option>
                    <option value="portfolio">Portfolio / Réalisations</option>
                    <option value="other">Autre</option>
                  </select>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white h-12 text-lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Envoyer ma demande
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <Card className="bg-white/10 backdrop-blur border-white/20 text-white">
              <CardHeader>
                <CardTitle className="text-lg">Besoin d'aide ?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-white/80 text-sm">
                  Notre équipe est disponible pour répondre à vos questions.
                </p>
                {agencyInfo?.phone && (
                  <a href={`tel:${agencyInfo.phone}`} className="flex items-center gap-3 p-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors">
                    <Phone className="w-5 h-5 text-cyan-400" />
                    <span>{agencyInfo.phone}</span>
                  </a>
                )}
                {agencyInfo?.email && (
                  <a href={`mailto:${agencyInfo.email}`} className="flex items-center gap-3 p-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors">
                    <Mail className="w-5 h-5 text-cyan-400" />
                    <span className="text-sm">{agencyInfo.email}</span>
                  </a>
                )}
              </CardContent>
            </Card>

            {/* Trust Badges */}
            <Card className="bg-white">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-[#1a365d] mb-4">Pourquoi nous choisir ?</h3>
                <ul className="space-y-3">
                  {[
                    'Réponse sous 24h',
                    'Devis détaillé gratuit',
                    '+150 projets réalisés',
                    '98% de clients satisfaits',
                    'Accompagnement personnalisé'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[#64748b]">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default QuoteRequestPage;
